/*
 * Copyright © 2015-2019 Serenova, LLC. All rights reserved.
 */

import { connect } from 'react-redux';
import { compose } from 'redux';
import { reduxForm } from 'redux-form/immutable';
import IntegrationsForm from './layout';
import { formValidation } from './validation';
import {
  getMsteamsSyncUserProgressData,
  getSelectedEntity,
  getSelectedEntityId,
  isCreating,
  isInherited,
  isMsteamsUserSyncing,
  msteamsUserSync,
  userHasPermissions,
  userHasUpdatePermission,
} from '../../../redux/modules/entities/selectors';
import {
  createFormName,
  formSubmission,
  getCurrentFormActiveStatus,
  getCurrentFormValueByFieldName,
  isFormDirty,
} from '../../../redux/modules/form/selectors';
import {
  getGoogleOauthClientId,
  getGoogleOauthClientSecret,
  getMsOauthClientId,
  getMsOauthClientSecret,
  getMsOauthPreferredUsername,
  getOauthAccessTokenResponse,
  selectIntegrationsFormInitialValues,
  selectTwilioRegions,
  getOptionsDropDown,
  getIntegrations,
} from '../../../redux/modules/entities/integrations/selectors';

import { getContactLayoutsExt } from '../../../redux/modules/userData/selectors';

import {
  copyCurrentEntity,
  fetchOAuthAccessToken,
  setImapHost,
  setOAuthCredentialId,
  setOAuthToken,
  setSmtpHost,
  setSmtpUser,
  syncUsersEntity,
  syncUsersEntityProgress,
  startSalesforceOauth,
  salesforceOauthReturnSuccess,
} from '../../../redux/modules/entities';

import { readContactLayouts } from '../../../redux/modules/entities/contactLayouts/actions';

const CreateIntegrationsForm = compose(
  connect((state) => createFormName(state)),
  reduxForm({
    onSubmit: formSubmission,
    validate: formValidation,
    destroyOnUnmount: true,
  })
)(IntegrationsForm);

export function mapDispatchToProps(dispatch) {
  let actions = {};
  // TODO: copy button is experimental feature and is feature flagged.
  if (location.hash.includes('alpha')) {
    actions.copy = () => dispatch(copyCurrentEntity());
  }
  return {
    ...actions,
    onSyncUsers: () => {
      dispatch(syncUsersEntity());
      dispatch(syncUsersEntityProgress());
    },
    fetchOAuthAccessToken: (body, fromListener) => dispatch(fetchOAuthAccessToken(body, fromListener)),
    readContactLayouts: () => dispatch(readContactLayouts()),
    setImapHost: (imapHost) => dispatch(setImapHost(imapHost)),
    setSmtpHost: (smtpHost) => dispatch(setSmtpHost(smtpHost)),
    setSmtpUser: (smtpUser) => dispatch(setSmtpUser(smtpUser)),
    setOAuthCredentialId: (token, fromListener) => dispatch(setOAuthCredentialId(token, fromListener)),
    setOAuthToken: (token, fromListener) => dispatch(setOAuthToken(token, fromListener)),
    onStartSalesforceOauth: ({ integrationId, loginUrl }) => dispatch(startSalesforceOauth(integrationId, loginUrl)),
    onSalesforceOauthReturnSuccess: ({ integrationId }) => dispatch(salesforceOauthReturnSuccess(integrationId)),
  };
}

export function mapStateToProps(state) {
  const properties = getCurrentFormValueByFieldName(state, 'properties');
  const isExtensionActive = getCurrentFormActiveStatus(state);
  const isHavingMsTeamsProperties = properties && properties.get('adAppId');
  const msTeamsIntegrationEnabled =
    isExtensionActive && isHavingMsTeamsProperties && isHavingMsTeamsProperties.length > 0;
  const integrations = getIntegrations(state);
    const rawIntegrationActive = getCurrentFormActiveStatus(state);
  // Coerce to boolean: handle Map, undefined, or boolean
  let isIntegrationActive;
  if (rawIntegrationActive && typeof rawIntegrationActive.get === 'function') {
    // Immutable Map or similar
    isIntegrationActive = !!rawIntegrationActive.get('active');
  } else {
    isIntegrationActive = !!rawIntegrationActive;
  }

  const syncUserData = getMsteamsSyncUserProgressData(state);
  let syncedUsers;
  if (syncUserData !== undefined && syncUserData?.currentState === 'Success') {
    syncedUsers = `${syncUserData.userExtnSyncSuccess}/${syncUserData.totalAdUsers}`;
  } else {
    syncedUsers = '-/-';
  }

  const syncUserProgressData = getMsteamsSyncUserProgressData(state);
  let syncedUsersProgress;
  if (syncUserProgressData && Object.keys(syncUserProgressData).length > 0) {
    syncedUsersProgress =
      syncUserProgressData.currentTask === 'Updating user extensions'
        ? `in progress - User Synced (${syncUserProgressData.userExtnSyncSuccess}/${syncUserProgressData.totalAdUsers})`
        : `in progress - ${syncUserProgressData.currentTask}`;
  } else {
    syncedUsersProgress = 'in progress';
  }

  return {
    authType: getCurrentFormValueByFieldName(state, 'authType'),
    contactLayouts: getContactLayoutsExt(state),
    currentEmail: properties && properties.get('smtpUser'),
    ctiEnabled: properties && properties.get('ctiEnabled'),
    dirty: isFormDirty(state),
    googleOauthClientId: getGoogleOauthClientId(state),
    googleOauthClientSecret: getGoogleOauthClientSecret(state),
    incomingType: properties && properties.get('incomingType'),
    inherited: isInherited(state),
    initialValues: selectIntegrationsFormInitialValues(state),
    integrationActive: isIntegrationActive,
    integrationType: getCurrentFormValueByFieldName(state, 'type'),
    isMsTeamsIntegrationEnabled: msTeamsIntegrationEnabled,
    isMsteamsUserSyncing: isMsteamsUserSyncing(state),
    isSaving: isCreating(state),
    isWebRtcEnabled: properties && properties.get('webRtc'),
    key: getSelectedEntityId(state),
    msOauthClientId: getMsOauthClientId(state),
    msOauthClientSecret: getMsOauthClientSecret(state),
    msOauthPreferredUsername: getMsOauthPreferredUsername(state),
    msteamsSyncUserData: syncedUsers,
    msteamsSyncUserProgressData: syncedUsersProgress,
    msteamsUserSync: msteamsUserSync(state),
    oauthCredential: properties && properties.get('oauthCredential'),
    oauthCredentialId: properties && properties.get('oauthCredentialId'),
    oauthCredentialResponse: getOauthAccessTokenResponse(state),
    regions: selectTwilioRegions(state),
    rtaEnabled: properties && properties.get('rtaEnabled'),
    salesforceAuthVersion: properties && properties.get('authVersion'),
    salesforceLoginUrl: properties && properties.get('loginUrl'),
    selectedEntity: getSelectedEntity(state),
    sidePanelUpdatePermissions: {
      twilioGlobalDialParams: userHasPermissions(state, ['TWILIO_GLOBAL_DIAL_PARAMS_MANAGE']),
      ecgGlobalDialParams: userHasPermissions(state, ['ECG_GLOBAL_DIAL_PARAMS_MANAGE']),
      msTeamsReadPermission: userHasPermissions(state, ['MSTEAMS_INTEGRATIONS_READ']),
      msTeamsUpdatePermission: userHasPermissions(state, ['MSTEAMS_INTEGRATIONS_UPDATE']),
      salesForceServiceCloudReadPermission:userHasPermissions(state, ['SALESFORCE_SERVICE_CLOUD_INTEGRATIONS_READ']),
      salesForceServiceCloudUpdatePermission:userHasPermissions(state, ['SALESFORCE_SERVICE_CLOUD_INTEGRATIONS_UPDATE'])
    },
    options: getOptionsDropDown(
      userHasPermissions(state, ['MSTEAMS_INTEGRATIONS_UPDATE']),
      userHasPermissions(state, ['SALESFORCE_SERVICE_CLOUD_INTEGRATIONS_UPDATE']),
      integrations),
    userHasUpdatePermission: userHasUpdatePermission(state),
    workItems: properties && properties.get('workItems'),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateIntegrationsForm);
